import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { bookingServiceAPI } from '../services/bookingServiceAPI';
import '../styles/PaymentSuccess.css';

function PaymentProcessing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const bookingId = searchParams.get('booking');

    if (!bookingId) {
      navigate('/');
      return;
    }

    checkPaymentStatus(bookingId);
  }, [searchParams, navigate]);

  const checkPaymentStatus = async (bookingId: string) => {
    try {
      setChecking(true);

      // Check if this is additional payment
      const paymentType = searchParams.get('type');
      const ticketCount = searchParams.get('count');

      // Wait longer for webhook to process (especially for additional payments)
      const waitTime = paymentType === 'additional' ? 5000 : 2000;
      await new Promise(resolve => setTimeout(resolve, waitTime));

      if (paymentType === 'additional') {
        // للدفع الإضافي، نحتاج للتحقق من طلب التذاكر الإضافية وليس الحجز الأصلي
        try {
          // نحاول 3 مرات للتحقق مع انتظار بين كل محاولة
          let lastOrder = null;
          let attempts = 0;
          const maxAttempts = 3;

          while (attempts < maxAttempts) {
            attempts++;
            console.log(`🔄 Checking additional payment status (attempt ${attempts}/${maxAttempts})...`);

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/additional-tickets/status?bookingId=${bookingId}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              lastOrder = data.lastOrder;

              if (lastOrder && lastOrder.paymentStatus === 'completed') {
                console.log('✅ Additional payment completed!');
                navigate(`/additional-payment-success/${bookingId}/${ticketCount || 1}`);
                return;
              } else if (lastOrder && lastOrder.paymentStatus === 'failed') {
                console.log('❌ Additional payment failed');
                navigate(`/additional-payment/${bookingId}`);
                return;
              } else if (lastOrder && lastOrder.paymentStatus === 'pending' && attempts < maxAttempts) {
                console.log(`⏳ Payment still pending, waiting... (attempt ${attempts})`);
                // انتظار إضافي قبل المحاولة التالية
                await new Promise(resolve => setTimeout(resolve, 3000));
                continue;
              }
            }

            // إذا وصلنا هنا والدفع ما زال pending أو فشل الAPI
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }

          // إذا انتهت المحاولات ولم نجد نجاح
          console.log('⚠️ Max attempts reached, payment status unclear, redirecting back');
          navigate(`/additional-payment/${bookingId}`);

        } catch (additionalError) {
          console.error('Error checking additional payment:', additionalError);
          navigate(`/additional-payment/${bookingId}`);
        }
      } else {
        // الدفع العادي - نفس المنطق السابق
        const bookingData = await bookingServiceAPI.getBooking(bookingId);

        if (bookingData && bookingData.booking) {
          if (bookingData.booking.paymentStatus === 'completed') {
            // Regular payment successful - redirect to success page
            navigate(`/payment-success?booking=${bookingId}`);
          } else if (bookingData.booking.paymentStatus === 'failed') {
            // Payment failed - redirect to failed page
            navigate(`/payment-failed?booking=${bookingId}&status=failed`);
          } else {
            // Payment still pending - redirect back to payment page
            console.log('⚠️ Payment still pending, redirecting to payment page');
            navigate(`/payment?booking=${bookingId}`);
          }
        } else {
          // Booking not found
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      // On error, redirect back to appropriate page
      const paymentType = searchParams.get('type');
      if (paymentType === 'additional') {
        navigate(`/additional-payment/${bookingId}`);
      } else {
        navigate(`/payment?booking=${bookingId}`);
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <motion.div
      className="payment-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <img src="/images/logo.png" alt="Fotomista" />
          </div>
          <div className="page-title">
            معالجة الدفع
          </div>
        </div>
      </nav>

      <div className="payment-container">
        <div className="payment-loading-container">
          <div className="payment-loading-content">
            <div className="payment-spinner">
              <div className="spinner-circle"></div>
              <div className="spinner-circle-2"></div>
              <div className="spinner-circle-3"></div>
            </div>
            <div className="loading-text">
              <h3>جاري التحقق من حالة الدفع</h3>
              <p>يرجى الانتظار قليلاً...</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default PaymentProcessing;